import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, Lock, Share2, ArrowUpDown } from 'lucide-react';
import { useProjectContext } from '../../contexts/ProjectContext';
import { format } from 'date-fns';
import { useClickOutside } from '../../hooks/useClickOutside';
import useEscapeKey from '../../hooks/useEscapeKey';
import { motion } from "framer-motion";
import { useAuth } from '../../hooks/useAuth';
import { projectService } from '../../services/project.service';
import { Project } from '../../types';
import { useNotifications } from '../../contexts/NotificationContext';
import { useActionSnackbar } from '../../contexts/ActionSnackbarContext'; // Added for snackbar
import { saveProjectId } from '../../store/persistence';

interface LoadProjectDialogProps {
  onClose: () => void;
}

export default function LoadProjectDialog({ onClose }: LoadProjectDialogProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false); // For initial projects fetch
  // const [loadingProjectId, setLoadingProjectId] = useState<string | null>(null); // Removed, snackbar will be used
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [sortColumn, setSortColumn] = useState<keyof Project | 'owner_email' | 'nodes_count'>('updated_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const { loadProject } = useProjectContext();
  const { showNotification } = useNotifications();
  const { showSnackbar } = useActionSnackbar(); // Added for snackbar
  const { user, isAdmin } = useAuth();
  
  const dialogRef = useClickOutside<HTMLDivElement>(onClose); 
  const selectedItemRef = useRef<HTMLButtonElement | null>(null);


  const loadProjects = useCallback(async () => {
    if (!user) {
      return;
    }
    setIsLoading(true);
    try {
      // Fetch only the user's projects
      const projects = await projectService.getUserProjects(false);
      setProjects(projects);
    } catch (error) {
      showNotification('error', 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  }, [showNotification, user]);

  // Add a function to load all projects (admin only)
  const loadAllProjects = useCallback(async () => {
    if (!user || !isAdmin) {
      return;
    }
    setIsLoading(true);
    try {
      // Use the existing API service to fetch all projects
      // The backend already has logic to return all projects for admin users
      const projects = await projectService.getUserProjects(true);
      setProjects(projects);
    } catch (error) {
      showNotification('error', 'Failed to load all projects');
      console.error('Error loading all projects:', error);
    } finally {
      setIsLoading(false);
    }
  }, [showNotification, user, isAdmin]);

  // Effect to load projects when component mounts or when user changes or when toggle changes
  useEffect(() => {
    if (user) {
      if (isAdmin && showAllProjects) {
        loadAllProjects();
      } else {
        loadProjects();
      }
    }
  }, [user, loadProjects, loadAllProjects, isAdmin, showAllProjects]);

  const handleProjectSelect = (project: Project) => {
    // Just select the project, don't load it yet
    setSelectedProject(project);
  };

  const handleLoadSelectedProject = useCallback(async () => {
    if (!selectedProject) return;

    // Show snackbar and close dialog immediately
    showSnackbar(`Loading project: ${selectedProject.name}...`);
    onClose(); // Close the dialog

    try {
      // Save the project ID to localStorage for persistence across page refreshes
      saveProjectId(selectedProject.id);
      
      // Load the project
      await loadProject(selectedProject.id);
      // No need for delay or setting loading project ID here
      
    } catch (error) {
      // If the project was not found, it might have been deleted
      if (error instanceof Error && error.message.includes('404')) {
        showNotification('error', 'Project not found. It may have been deleted.');
        // Refresh the projects list to remove the deleted project
        await loadProjects(); // loadProjects is from outer scope, ensure its deps are correct
        // Remove the deleted project from the local state as well
        setProjects(prevProjects => prevProjects.filter(p => p.id !== selectedProject.id)); // setProjects from outer scope
      } else {
        showNotification('error', `Failed to load project: ${selectedProject.name}`);
      }
      // setLoadingProjectId is removed, no finally block needed for it
    }
  }, [selectedProject, loadProject, onClose, showNotification, showSnackbar, loadProjects]);

  const handleSort = (column: keyof Project | 'owner_email' | 'nodes_count') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedAndFilteredProjects = projects
    .filter((project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      let valA: any;
      let valB: any;

      if (sortColumn === 'owner_email') {
        valA = a.owner?.email?.toLowerCase() || '';
        valB = b.owner?.email?.toLowerCase() || '';
      } else if (sortColumn === 'nodes_count') {
        valA = a.nodes.length;
        valB = b.nodes.length;
      } else {
        valA = a[sortColumn as keyof Project];
        valB = b[sortColumn as keyof Project];
      }
      
      // Ensure consistent type for comparison, especially for dates or numbers
      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const filteredProjects = sortedAndFilteredProjects; // Use the new sorted list

  useEscapeKey(onClose); // Use escape key to close the dialog

  // Effect to manage selectedProject when filteredProjects changes (e.g., initial load, search)
  useEffect(() => {
    if (filteredProjects.length > 0) {
      const currentSelectedId = selectedProject?.id;
      const isSelectedInFiltered = currentSelectedId ? filteredProjects.some(p => p.id === currentSelectedId) : false;
      
      if (!isSelectedInFiltered) {
        // If nothing is selected, or the selected item is no longer in the filtered list,
        // select the first item of the current filtered list.
        setSelectedProject(filteredProjects[0]);
      }
      // If something is selected and it's still in the list, keep it selected.
    } else {
      // No projects in the filtered list, so clear selection.
      setSelectedProject(null);
    }
  }, [filteredProjects]); 

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    // Don't interfere with typing in the search input for arrow keys
    if (event.target instanceof HTMLInputElement && event.target.type === 'text') {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        return; // Allow default behavior for input field navigation
      }
    }

    if (filteredProjects.length === 0 && !(event.key === 'Escape')) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
      event.preventDefault(); 
      const currentSelected = selectedProject;
      let currentIndex = currentSelected ? filteredProjects.findIndex(p => p.id === currentSelected.id) : -1;

      if (event.key === 'ArrowDown') {
        if (filteredProjects.length === 0) return;
        const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % filteredProjects.length;
        setSelectedProject(filteredProjects[nextIndex]);
      } else if (event.key === 'ArrowUp') {
        if (filteredProjects.length === 0) return;
        const prevIndex = currentIndex === -1 ? filteredProjects.length - 1 : (currentIndex - 1 + filteredProjects.length) % filteredProjects.length;
        setSelectedProject(filteredProjects[prevIndex]);
      } else if (event.key === 'Enter') {
        if (currentSelected) {
          handleLoadSelectedProject();
        }
      }
    }
  }, [filteredProjects, selectedProject, handleLoadSelectedProject]);
  
  // Effect to focus the dialog when it becomes visible to enable keyboard navigation
  useEffect(() => {
    // if (isVisible && dialogRef.current) { // isVisible removed
    if (dialogRef.current) { // Assume if rendered, it's visible
      setTimeout(() => dialogRef.current?.focus(), 0);
    }
  }, [dialogRef]); // dialogRef is stable

  // Effect to scroll the selected item into view
  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedProject]);

  return (
    <motion.div
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }} // Consistent overlay transition
    >
      <motion.div
        ref={dialogRef}
        onKeyDown={handleKeyDown}
        tabIndex={-1} // Make it focusable
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[960px] mx-4 border border-gray-200 dark:border-gray-700 focus:outline-none"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        {/* Header */}
        <div className="px-6 pt-5 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Open Project</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"> {/* Changed to onClose */}
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Search and Admin Controls */}
            <div className="p-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search projects..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
                  />
                </div>
                
                {/* Admin toggle for showing all projects */}
                {isAdmin && (
                  <div className="flex items-center">
                    <div 
                      className="relative flex items-center justify-center mr-2 cursor-pointer"
                      onClick={() => setShowAllProjects(!showAllProjects)}
                    >
                      <input
                        type="checkbox"
                        id="show-all-projects"
                        checked={showAllProjects}
                        onChange={(e) => setShowAllProjects(e.target.checked)}
                        className="sr-only"
                      />
                      <motion.div
                        initial={false}
                        animate={{
                          backgroundColor: showAllProjects
                            ? 'rgba(59, 130, 246, 1)'
                            : 'rgba(59, 130, 246, 0)',
                          borderColor: showAllProjects
                            ? 'rgb(59, 130, 246)'
                            : 'rgb(209, 213, 219)'
                        }}
                        className="h-4 w-4 rounded border-2 flex items-center justify-center"
                      >
                        {showAllProjects && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="text-white"
                          >
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8 11.5L11 15L16 9"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </motion.div>
                        )}
                      </motion.div>
                    </div>
                    <label 
                      htmlFor="show-all-projects" 
                      className="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowAllProjects(!showAllProjects);
                      }}
                    >
                      All projects
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Projects List Wrapper*/}
            <div className="px-4">
              <div className='rounded-md overflow-hidden border border-gray-300 dark:border-gray-600 dark:bg-gray-700/40'>
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
                  {[
                    { label: 'Name', key: 'name', span: 6 },
                    { label: 'Last Modified', key: 'updated_at', span: 2 },
                    { label: 'Owner', key: 'owner_email', span: 3 },
                    { label: 'Nodes', key: 'nodes_count', span: 1, align: 'right' },
                  ].map((col) => (
                    <div
                      key={col.key}
                      className={`col-span-${col.span} text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 flex items-center ${col.align === 'right' ? 'justify-end' : ''}`}
                      onClick={() => handleSort(col.key as keyof Project | 'owner_email' | 'nodes_count')}
                    >
                      {col.label}
                      {sortColumn === col.key && (
                        <ArrowUpDown className={`ml-1 h-3 w-3 ${sortDirection === 'asc' ? 'transform rotate-0' : 'transform rotate-180'}`} />
                      )}
                      {sortColumn !== col.key && (
                        <ArrowUpDown className="ml-1 h-3 w-3 opacity-30" />
                      )}
                    </div>
                  ))}
                </div>

                <div className="h-[400px] overflow-y-auto">
                  {isLoading ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading projects...</div>
                  ) : filteredProjects.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      {searchQuery ? 'No projects found' : 'No projects yet'}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredProjects.map((project) => (
                        <button
                          key={project.id}
                          ref={project.id === selectedProject?.id ? selectedItemRef : null}
                          onClick={() => handleProjectSelect(project)}
                          className={`w-full text-left hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none transition-colors duration-150 ${
                            selectedProject?.id === project.id ? 'bg-blue-100 dark:bg-blue-600/20 hover:bg-blue-200 dark:hover:bg-blue-600/50' : ''
                          }`}
                          onDoubleClick={handleLoadSelectedProject}
                        >
                          <div className="grid grid-cols-12 gap-4 px-4 py-3 items-center">
                            {/* Column 1: Name and Description */}
                            <div className="col-span-6">
                              <div className="flex items-center gap-2">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {project.name}
                                </h3>
                                {project.is_locked && (
                                  <Lock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                )}
                                {project.shared_with.length > 0 && (
                                  <Share2 className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                )}
                                {user && project.owner && project.owner_id !== user.id && (
                                  <div className="relative group">
                                    <Lock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                    <div className="absolute left-0 top-full mt-2 w-48 bg-gray-800 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                      You have view-only access to this project
                                    </div>
                                  </div>
                                )}
                              </div>
                              {project.description && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2 max-w-full overflow-hidden text-ellipsis">
                                  {project.description}
                                </p>
                              )}
                            </div>
                            
                            {/* Column 2: Last Updated */}
                            <div className="col-span-2 text-xs text-gray-500 dark:text-gray-400">
                              {format(new Date(project.updated_at), "MMM d, yyyy HH:mm")}
                            </div>
                            
                            {/* Column 3: Owner */}
                            <div className="col-span-3 text-xs text-gray-500 dark:text-gray-400 truncate">
                              {user && project.owner && project.owner_id !== user.id ? (
                                <span>{project.owner.email}</span>
                              ) : (
                                <span>You</span>
                              )}
                            </div>
                            
                            {/* Column 4: Nodes Count */}
                            <div className="col-span-1 text-xs text-gray-500 dark:text-gray-400 text-right">
                              {project.nodes.length}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-4 rounded-b-lg">
              <div className="flex justify-between items-center space-x-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Hint: Double-click a project to open it.
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={onClose} // Changed to onClose
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLoadSelectedProject}
                  disabled={!selectedProject} // loadingProjectId removed
                  className={`px-4 py-2 text-sm font-medium rounded-md flex items-center justify-center min-w-[100px] transition-colors ${
                    !selectedProject
                      ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {/* Loading indicator removed from button */}
                  Open Project
                </button>
                </div>
              </div>
            </div>
      </motion.div>
    </motion.div>
  );
}
